import React, { useEffect, useState, useCallback } from "react";
import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { ArrowLeft } from "lucide-react";
import logoutIcon from "../../assets/icons/logout.svg";
import success_tick from "../../assets/icons/stepSuccess.svg";
import angle_down from "../../assets/icons/angle-down.svg";
import { Input } from "../../components/ui/input-field";
import SimpleSelect from "../../components/SimpleSelect";
import PhoneInput from "../../components/PhoneInput";
import veriff from "../../assets/images/veriff.png";
import camera from "../../assets/images/camera.svg";
import sync from "../../assets/icons/sync.svg";
import AllSet from "../../components/AllSet";
import countries from "../../assets/countries/data.json";
import { api } from "../../services/axiosService";
import Toaster from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../context/AuthContext";
import { DateInput } from "../../components/ui/date-input";
import SpinningLoader from "../../components/SpinningLoader";
import {
  validateBirthDate,
  getMinimumValidBirthDate,
} from "../../utils/ageRestriction";

const steps = [
  { label: "1 Referral Code", id: 1 },
  { label: "2 Personal details", id: 2 },
  { label: "3 Financial details", id: 3 },
  { label: "4 Verify identity", id: 4 },
];

const headings = [
  { id: 1, label: "Referral code" },
  { id: 2, label: "Personal details" },
  { id: 3, label: "Financial details" },
  { id: 4, label: "Verify identity" },
];

const countryOptions = countries.countries;

const statusMessages = {
  created:
    "Verification session created. Please start the verification process.",
  started: "Verification process has started. Please complete the steps.",
  submitted: "Your documents have been submitted and are under review.",
  approved: "Your identity has been successfully verified!",
  declined: "Your identity verification was declined.",
  resubmission_requested: "Additional documents are required. Please resubmit.",
  expired: "Verification session has expired. Please start a new one.",
  abandoned: "Verification process was abandoned. Please start again.",
  review: "Your documents are under review. We'll notify you once completed.",
};

const formatCurrencyForDisplay = (currency) => {
  const currencyMap = new Map([
    ["ZAR", "ZAR - South African Rand"],
    ["USD", "USD - United States Dollar"],
    ["EUR", "EUR - Euro"],
    ["JPY", "JPY - Japanese Yen"],
    ["GBP", "GBP - British Pound Sterling"],
    ["AUD", "AUD - Australian Dollar"],
    ["CAD", "CAD - Canadian Dollar"],
    ["CHF", "CHF - Swiss Franc"],
    ["CNY", "CNY - Chinese Yuan"],
    ["HKD", "HKD - Hong Kong Dollar"],
    ["NZD", "NZD - New Zealand Dollar"],
  ]);
  return currencyMap.get(currency) || currency;
};

const parseMonthlySalaryRange = (range) => {
  if (!range) return { currency: "", monthlySalaryRange: "" };
  const parts = range.split("-");
  if (parts.length >= 2) {
    const currency = formatCurrencyForDisplay(parts[0].trim());
    const salaryRange = parts.slice(1).join("-").trim();
    return { currency, monthlySalaryRange: salaryRange };
  }
  return { currency: "", monthlySalaryRange: range };
};

export default function CompleteProfile() {
  const { toast } = useToast();
  const { handleLogout, user, getAccountDetails, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loader, setLoader] = useState(false);
  const [values, setValues] = useState({
    referralCode: "",
    name: "",
    surname: "",
    title: "",
    idNumber: "",
    dateOfBirth: "",
    phoneNumber: "",
    countryCode: "+27",
    country: "South Africa",
    address: "",
    marketingConsent: false,
    termsAccepted: false,
    employmentStatus: "",
    primarySourceOfFunds: "",
    monthlySalaryRange: "",
    currency: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [allSet, setAllSet] = useState(false);
  const [activeCountryModal, setActiveCountryModal] = useState(false);
  const [notify, setNotify] = useState(false);
  const [isFirstCountrySelection, setIsFirstCountrySelection] = useState(true);
  const [veriffStatus, setVeriffStatus] = useState(null);
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [statusPopupMessage, setStatusPopupMessage] = useState("");
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [financialDetailsCompleted, setFinancialDetailsCompleted] =
    useState(false);

  const checkVeriffStatus = useCallback(
    async (forceRefresh = false) => {
      try {
        let updatedUser;
        if (forceRefresh) {
          updatedUser = await getAccountDetails();
        } else {
          updatedUser = user;
        }
        const statusData = updatedUser?.veriffDetails?.status || "created";
        if (statusData) {
          setVeriffStatus(statusData);
          handleStatusChange(statusData);
          setLoader(false);
        }
      } catch (err) {
        console.error("Error checking Veriff status:", err);
      }
    },
    [user, getAccountDetails]
  );

  useEffect(() => {
    if (user && !isDataLoaded) {
      if (user.personal_info && Object.keys(user.personal_info).length > 0) {
        const { currency, monthlySalaryRange } = parseMonthlySalaryRange(
          user.monthly_salary_range
        );
        const dob = user.personal_info?.dob;
        const userData = {
          referralCode: "",
          name: user.personal_info?.name || "",
          surname: user.personal_info?.lastname || "",
          title: user.personal_info?.title || "",
          idNumber: user.personal_info?.identity_number || "",
          dateOfBirth: dob ? dob.split("T")[0] : "",
          phoneNumber: user.phone_number?.number || "",
          countryCode: user.phone_number?.country_code || "+27",
          country: user.address?.country || "South Africa",
          address: user.address?.address || "",
          marketingConsent: user.marketing_consent || false,
          termsAccepted: user.terms_conditions || false,
          employmentStatus: user.employmentStatus || "",
          primarySourceOfFunds: user.funds_source || "",
          monthlySalaryRange: monthlySalaryRange || "",
          currency: currency || "",
        };

        setValues(userData);
        setIsDataLoaded(true);

        if (user.countryAcceptance !== "accepted") {
          setNotify(true);
        }

        if (user.personal_info?.name && user.personal_info?.lastname) {
          setCurrentStep(4);
          checkVeriffStatus();
          setFinancialDetailsCompleted(true);
        }
      }
    }
  }, [user, isDataLoaded, checkVeriffStatus]);

  useEffect(() => {
    if (currentStep === 4 && !allSet) {
      checkVeriffStatus();
    }
  }, [currentStep, allSet, checkVeriffStatus]);

  const handleStatusChange = (status) => {
    const noPopupStatuses = [
      "created",
      "started",
      "resubmission_requested",
      "expired",
      "abandoned",
    ];
    const waitingStatuses = ["review"];
    const statusMessagesMap = new Map(Object.entries(statusMessages));
    const getStatusMessage = (statusValue) => {
      return statusMessagesMap.get(statusValue) || "";
    };
    if (status === "approved" || status === "submitted") {
      setAllSet(true);
      setShowStatusPopup(true);
      setStatusPopupMessage(getStatusMessage(status));
    } else if (status === "declined") {
      setShowStatusPopup(true);
      setStatusPopupMessage(getStatusMessage(status));
    } else if (waitingStatuses.includes(status)) {
      setShowStatusPopup(true);
      setStatusPopupMessage(getStatusMessage(status));
    } else if (noPopupStatuses.includes(status)) {
      setShowStatusPopup(false);
    }
  };

  const backToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "country" && isFirstCountrySelection) {
        const matched = countryOptions.find((opt) => opt.name === value);
        if (matched && matched.value !== prev.countryCode) {
          updated.countryCode = matched.value;
        }
      }
      return updated;
    });

    if (Object.prototype.hasOwnProperty.call(errors, name)) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (value, countryCode) => {
    setValues((prev) => {
      const updated = {
        ...prev,
        phoneNumber: value,
        countryCode,
      };

      if (isFirstCountrySelection) {
        const matched = countryOptions.find((opt) => opt.value === countryCode);
        if (matched && matched.name !== prev.country) {
          updated.country = matched.name;
        }
        setIsFirstCountrySelection(false);
      }
      return updated;
    });

    if (errors.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const validateStep2 = async () => {
    const cleanedPhoneNumber = values.phoneNumber.replace(/^0+/, "");
    if (cleanedPhoneNumber !== values.phoneNumber) {
      setValues((prev) => ({ ...prev, phoneNumber: cleanedPhoneNumber }));
    }
    const newErrors = {};

    const phoneNumber = `${values.countryCode} ${cleanedPhoneNumber}`;

    if (!values.name.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z\s]{1,50}$/.test(values.name.trim()))
      newErrors.name = "Name should only contain letters (max 50 characters)";

    if (!values.surname.trim()) newErrors.surname = "Surname is required";
    else if (!/^[a-zA-Z\s]{1,50}$/.test(values.surname.trim()))
      newErrors.surname =
        "Surname should only contain letters (max 50 characters)";

    if (!values.title) newErrors.title = "Title is required";
    if (!values.idNumber) newErrors.idNumber = "ID/Passport number is required";
    else if (values.idNumber.length < 5 || values.idNumber.length > 20)
      newErrors.idNumber =
        "ID/Passport number must be between 5 and 20 characters";
    else if (!/^[a-zA-Z0-9]+$/.test(values.idNumber))
      newErrors.idNumber =
        "ID/Passport number can only contain letters and numbers";

    if (!values.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      if (phoneNumber.length !== 13) {
        newErrors.phoneNumber =
          "Invalid phone number format for selected country";
      } else {
        if (!user.personal_info?.name) {
          try {
            const response = await api.post("/api/v1/user/validate-account", {
              phoneNumber: values.phoneNumber,
              countryCode: values.countryCode,
            });

            if (response.data && !response.data.status) {
              newErrors.phoneNumber =
                response.data.message ||
                "Phone number is already registered or invalid";
            }
          } catch (error) {
            console.error("Phone validation error:", error);
            if (
              typeof error === "string" &&
              (error.includes("<!DOCTYPE") || error.includes("<html"))
            ) {
              newErrors.phoneNumber =
                "Unable to validate phone number. Please try again.";
              return;
            }
            newErrors.phoneNumber =
              error?.charAt(0).toUpperCase() + error?.slice(1) ||
              "Failed to validate phone number";
          }
        }
      }
    }

    const birthDateValidation = validateBirthDate(values.dateOfBirth);
    if (!birthDateValidation.isValid) {
      newErrors.dateOfBirth =
        birthDateValidation.error || "Invalid date of birth";
    }

    if (!values.country) newErrors.country = "Country is required";
    if (!values.address.trim()) newErrors.address = "Address is required";

    setErrors(newErrors);

    const matchedCountryName = countryOptions.find(
      (opt) => opt.name === values.country
    );
    if (matchedCountryName && !matchedCountryName.isActive) {
      setActiveCountryModal(true);
      return false;
    }
    if (Object.keys(newErrors).length === 0) setFinancialDetailsCompleted(true);

    return Object.keys(newErrors).length === 0;
  };

  const handleReferralCodeSubmit = (e) => {
    e.preventDefault();
    setCurrentStep(2);
  };

  const handlePersonalDetailsSubmit = async (e) => {
    if (e) e.preventDefault();
    if (await validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleFinancialDetailsSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (user.personal_info?.name) {
      setCurrentStep(4);
      return;
    }

    setIsLoading(true);
    try {
      await api.post("/api/v1/user/complete-account", {
        referralCode: values.referralCode,
        firstName: values.name,
        lastName: values.surname,
        title: values.title,
        idNumber: values.idNumber,
        dateOfBirth: `${values.dateOfBirth}T00:00:00Z`,
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode,
        country: values.country,
        address: values.address,
        employmentStatus: values.employmentStatus,
        sourceOfFunds: values.primarySourceOfFunds,
        monthlySalaryRange: `${values.currency.split(" - ")[0]}-${values.monthlySalaryRange}`,
        marketingConsent: values.marketingConsent,
        termsAndConditions: values.termsAccepted,
      });
      await refreshUser();
      if (e) setCurrentStep(4);
    } catch (error) {
      console.log(error);
      toast({
        description:
          error?.charAt(0).toUpperCase() + error?.slice(1) ||
          "An error occurred",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createVeriffSession = async () => {
    try {
      const response = await api.get("/api/v1/veriff/start-verification");
      if (response.data.status) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  const initiateVeriff = async () => {
    setIsLoading(true);
    const sessionData = await createVeriffSession();
    await setLoader(false);
    if (!sessionData || !sessionData?.url) {
      setIsLoading(false);
      toast({
        description: "Failed to create verification session",
        variant: "error",
      });
      return;
    }
    const sessionUrl = sessionData.url;
    createVeriffFrame({
      url: sessionUrl,
      onEvent: async (msg) => {
        switch (msg) {
          case MESSAGES.STARTED:
            break;
          case MESSAGES.CANCELED:
            break;
          case MESSAGES.FINISHED:
            setLoader(true);
            setTimeout(() => {
              checkVeriffStatus(true);
            }, 3000);
            break;
          default:
            break;
        }
      },
      onReload: () => {
        window.location.reload();
      },
    });

    setIsLoading(false);
  };

  const canStartVerification = () => {
    const allowedStatuses = [
      null,
      "created",
      "started",
      "resubmission_requested",
      "expired",
      "abandoned",
    ];
    return allowedStatuses.includes(veriffStatus);
  };

  const getVerificationButtonText = () => {
    if (!veriffStatus) return "I'm ready";

    const statusTextMap = new Map([
      ["created", "Start Verification"],
      ["started", "Continue Verification"],
      ["resubmission_requested", "Resubmit Documents"],
      ["expired", "Start New Verification"],
      ["abandoned", "Start Verification"],
    ]);

    return statusTextMap.get(veriffStatus) || "I'm ready";
  };

  const isStep2Complete = () => {
    const requiredFields = [
      "name",
      "surname",
      "title",
      "idNumber",
      "dateOfBirth",
      "phoneNumber",
      "country",
      "address",
      "termsAccepted",
    ];
    return requiredFields.every((field) => {
      const descriptor = Object.getOwnPropertyDescriptor(values, field);
      if (descriptor && descriptor.value) {
        return descriptor.value;
      }
      return false;
    });
  };

  const handleAddressSelect = (address) => {
    setValues((prev) => ({
      ...prev,
      address: address,
    }));
  };

  return (
    <>
      <div className="min-h-screen flex flex-col overflow-y-auto w-full ">
        <div className="flex  border-0 justify-between items-center pt-[3.75rem] pb-[1.75rem] lg:pt-[1.75rem] lg:px-16 px-5 lg:pb-[1.5rem] lg:border lg:border-b lg:border-b-[#EBE9EF]">
          <button
            className={`w-12 h-12 flex items-center justify-center rounded-full border border-[#EBE9EF] ${currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={backToPreviousStep}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-5 h-5 text-[#6D6584]" />
          </button>

          <p className="font-plusjakarta font-[600] font-semibold text-[16px] lg:text-[20px] leading-[24px] tracking-normal text-center lg:block hidden text-[#33125F]">
            Complete Profile
          </p>
          <p className="font-plusjakarta font-[600] font-semibold text-[16px] lg:text-[20px] leading-[24px] tracking-normal text-center text-[#33125F] block lg:hidden">
            {headings.find((s) => s.id === currentStep)?.label}
          </p>
          <button
            onClick={handleLogout}
            className="w-12 h-12 flex items-center lg:hidden block justify-center rounded-full border border-[#EBE9EF]"
          >
            <img src={logoutIcon} alt="" className="w-[16px] h-[16px]" />
          </button>

          <button
            onClick={handleLogout}
            className="rounded-full border lg:block hidden border-[#EBE9EF] flex items-center justify-center px-5 py-[13px] text-[14px] font-medium text-[#6D6584]"
          >
            Log out
          </button>
        </div>
        <div className=" h-[4px] bg-gray-200 rounded-full w-full lg:hidden block ">
          <div
            className="h-[4px] bg-[#CFB0F1] "
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
        <div className="z-10 mt-[1.5rem]  flex flex-col  items-center justify-center  ">
          <div className="w-full max-w-[1005px] mx-auto mt-8 lg:mb-8 px-6">
            <div className="lg:hidden flex justify-between items-center">
              <p className="font-plusjakarta font-semibold font-[600] text-[16px] text-[#CFB0F1]">
                {currentStep} / {steps.length}
              </p>
            </div>
            <div className="hidden lg:flex justify-center items-center gap-4">
              {steps.map((step, index) => {
                const isActive = step.id === currentStep;
                const isEnabled =
                  step.id === 1 ||
                  (step.id === 2 && currentStep >= 2) ||
                  (step.id === 3 && isStep2Complete()) ||
                  (step.id === 4 &&
                    isStep2Complete() &&
                    financialDetailsCompleted);

                const isCompleted =
                  (step.id === 1 && currentStep > 1) ||
                  (step.id === 2 && isStep2Complete()) ||
                  (step.id === 3 &&
                    isStep2Complete() &&
                    financialDetailsCompleted) ||
                  (step.id === 4 && veriffStatus === "approved") ||
                  step.id < currentStep;

                return (
                  <React.Fragment key={step.id}>
                    <button
                      onClick={() => {
                        if (step.id === 3) handlePersonalDetailsSubmit();
                        else if (isEnabled) setCurrentStep(step.id);
                      }}
                      disabled={!isEnabled}
                      className={`
                        font-plusjakarta font-medium text-[14px] leading-[20px] text-center flex items-center
                        ${isActive ? "text-[#57249C]" : ""}
                        ${isEnabled && !isActive ? "text-[#0D0B26]" : ""}
                        ${!isEnabled ? "text-[#ABB7C2] cursor-not-allowed" : "hover:text-[#57249C]"}
                      `}
                    >
                      {isCompleted ? (
                        <img
                          src={success_tick}
                          alt="Completed"
                          className="w-5 h-5"
                        />
                      ) : (
                        <span className="font-roboto font-medium">
                          {step.id}
                        </span>
                      )}
                      <span className="ml-3">
                        {step.label.replace(/^\d+\s*/, "")}
                      </span>
                    </button>

                    {index < steps.length - 1 && (
                      <div className="text-gray-400 w-[40px] mx-2 border-t border-[#CFD6DC] rotate-[-60deg] origin-center"></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {currentStep === 1 && (
            <div className="lg:max-w-[628px] w-full lg:p-6 px-6">
              <h2 className="font-plusjakarta block font-semibold text-[32px] leading-[40px] tracking-normal lg:text-center text-[#33125F] lg:my-5 my-2">
                Got a referral code?
              </h2>
              <p className="text-[#423E51] mb-10 font-plusjakarta font-normal text-[16px] leading-[24px] tracking-normal lg:text-center text-left">
                If someone referred you to us, share their code here.
              </p>
              <Input
                name="referralCode"
                type="text"
                text="Referral code (optional)"
                placeholder="e.g. 8EABC1234"
                value={values.referralCode}
                onChange={handleChange}
                state={errors?.referralCode ? "error" : "default"}
                helperText={errors?.referralCode || ""}
                className="!py-[27px] border-[1px] text-[16px] font-plusjakarta rounded-[16px]"
              />
              <button
                className={`bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] flex items-center justify-center text-white font-semibold text-[14px] font-[400] mt-10 px-8 lg:py-[11px] py-[12px] rounded !w-[90%] lg:max-w-[150px] ${isLoading ? "cursor-not-allowed opacity-70" : ""} absolute bottom-28 lg:relative lg:bottom-0 left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 rounded-full transition duration-300 ease-in-out  hover:scale-[1.1]`}
                onClick={handleReferralCodeSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          )}
          {currentStep === 2 && (
            <div className="lg:max-w-[628px] w-full lg:p-0 px-6">
              <h2 className="font-plusjakarta block font-semibold text-[32px] leading-[40px] tracking-normal lg:text-center text-[#33125F] lg:my-5 mt-2 mb-8">
                Personal details
              </h2>
              <div className="w-[100%] flex lg:flex-row flex-col gap-3">
                <div className="lg:w-[33%] w-full order-2 lg:order-2">
                  <Input
                    name="name"
                    type="text"
                    text="Name"
                    placeholder="Alex"
                    value={values.name}
                    onChange={handleChange}
                    state={errors?.name ? "error" : "default"}
                    helperText={errors?.name || ""}
                    className="!py-[27px] border-[1px] text-[16px] font-plusjakarta rounded-[16px]"
                  />
                </div>
                <div className="lg:w-[33%] w-full order-3 lg:order-3">
                  <Input
                    name="surname"
                    type="text"
                    text="Surname"
                    placeholder="Calamas"
                    value={values.surname}
                    onChange={handleChange}
                    state={errors?.surname ? "error" : "default"}
                    helperText={errors?.surname || ""}
                    className="!py-[27px] border-[1px] text-[16px] font-plusjakarta rounded-[16px]"
                  />
                </div>
                <div className="lg:w-[33%] w-full order-1 lg:order-1">
                  <SimpleSelect
                    label="Title"
                    placeholder="Select your title"
                    options={["Mr.", "Mrs.", "Miss", "Dr.", "Prof.", "Other"]}
                    icon={
                      <img
                        src={angle_down}
                        className="w-[16px] h-[16px]"
                        alt="dropdown"
                      />
                    }
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    state={errors?.title ? "error" : "default"}
                    helperText={errors?.title || ""}
                  />
                </div>
              </div>
              <div className="w-[100%] flex gap-3 lg:flex-row flex-col mb-5 mt-5">
                <div className="lg:w-[50%] w-full">
                  <Input
                    name="idNumber"
                    type="text"
                    text="ID/Passport number"
                    placeholder="Enter your ID or Passport number"
                    value={values.idNumber}
                    onChange={handleChange}
                    state={errors?.idNumber ? "error" : "default"}
                    helperText={errors?.idNumber || ""}
                    className="!py-[27px] border-[1px] text-[16px] font-plusjakarta rounded-[16px]"
                  />
                </div>
                <div className="lg:w-[50%] w-full">
                  <DateInput
                    name="dateOfBirth"
                    value={values.dateOfBirth}
                    onChange={(value) =>
                      handleChange({ target: { name: "dateOfBirth", value } })
                    }
                    label="Date of birth"
                    placeholder="Select your date of birth"
                    error={errors?.dateOfBirth}
                    maxDate={getMinimumValidBirthDate()}
                    minDate={new Date("1900-01-01")}
                  />
                </div>
              </div>
              <div className="w-[100%] flex gap-3 lg:flex-row flex-col mb-5 mt-5">
                <div className="lg:w-[50%] w-full">
                  <PhoneInput
                    countryOptions={countryOptions}
                    value={values.phoneNumber}
                    onChange={handlePhoneChange}
                    state={errors?.phoneNumber ? "error" : "default"}
                    helperText={errors?.phoneNumber || ""}
                    country={values.country}
                    isFirstCountrySelection={isFirstCountrySelection}
                    setIsFirstCountrySelection={setIsFirstCountrySelection}
                    countryCode={values.countryCode}
                  />
                </div>
                <div className="lg:w-[50%] w-full">
                  <SimpleSelect
                    searchable={true}
                    label="Which country do you live in?"
                    placeholder="Select your country"
                    options={countryOptions.map((country) => country.name)}
                    optionsIcons={countryOptions.map((flag) => flag.flag)}
                    icon={
                      <img
                        src={angle_down}
                        className="w-[16px] h-[16px]"
                        alt="dropdown"
                      />
                    }
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    state={errors?.country ? "error" : "default"}
                    helperText={errors?.country || ""}
                  />
                </div>
              </div>
              <div className="w-[100%] flex gap-3 mb-5 mt-5">
                <Input
                  name="address"
                  type="text"
                  text="What is your address?"
                  placeholder="97 Central Street, Houghton"
                  value={values.address}
                  onChange={handleChange}
                  onAddressSelect={handleAddressSelect}
                  state={errors?.address ? "error" : "default"}
                  helperText={errors?.address || ""}
                  enableAddressLookup={true}
                  className="!py-[27px] border-[1px] text-[16px] font-plusjakarta rounded-[16px]"
                />
              </div>
              <div className="flex items-center gap-5 lg:mt-12 mt-8">
                <div
                  className={`
                    w-8 h-8 rounded-[4px] border-2 flex items-center justify-center
                    ${values.marketingConsent ? "bg-[#33125F] border-[#33125F]" : "border-[#9D96AF] bg-white"}
                    transition-colors cursor-pointer
                  `}
                  onClick={() =>
                    setValues((prev) => ({
                      ...prev,
                      marketingConsent: !prev.marketingConsent,
                    }))
                  }
                >
                  {values.marketingConsent && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414L8.414 15 4.293 10.879a1 1 0 111.414-1.414L8.414 12.172l7.879-7.879a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>

                <p className="font-plusjakarta font-normal text-[16px] leading-[24px] text-[#6D6584] w-[80%] lg:w-auto">
                  I consent to receive marketing material from 80eight
                </p>
              </div>
              <div className="flex items-center flex-wrap gap-5 lg:mt-5 mt-8 lg:mb-0 mb-10">
                <div
                  className={`
                    w-8 h-8 rounded-[4px] border-2 flex items-center justify-center
                    ${values.termsAccepted ? "bg-[#33125F] border-[#33125F]" : "border-[#9D96AF] bg-white"}
                    transition-colors cursor-pointer
                  `}
                  onClick={() =>
                    setValues((prev) => ({
                      ...prev,
                      termsAccepted: !prev.termsAccepted,
                    }))
                  }
                >
                  {values.termsAccepted && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414L8.414 15 4.293 10.879a1 1 0 111.414-1.414L8.414 12.172l7.879-7.879a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <p className="font-plusjakarta font-normal text-[16px] leading-[24px] text-[#6D6584] w-[80%] lg:w-auto">
                  I accept the{" "}
                  <a
                    href="https://80eight.io/app-terms-of-use"
                    target="_blank"
                    without
                    rel="noreferrer"
                    className="font-[600] ml-1 underline text-[#33125F] "
                  >
                    Terms and Conditions
                  </a>
                </p>
              </div>
              <div className="w-[100%] gap-5 flex justify-center items-center mt-8">
                <button
                  className={`bg-transparent border border-[#EBE9EF] text-[#6D6584] flex items-center justify-center font-semibold text-[14px] font-[400] lg:my-10 my-5 px-8 lg:py-[11px] py-[12px] rounded !w-[90%] lg:max-w-[150px] rounded-full lg:block hidden transition duration-300 ease-in-out hover:scale-[1.1] hover:border-[#57249C] hover:text-[#57249C] ${currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={backToPreviousStep}
                  disabled={currentStep === 1}
                >
                  Back
                </button>
                <button
                  className={`
                    ${
                      values.termsAccepted && !isLoading
                        ? "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white"
                        : "bg-[#F2F1F4] cursor-not-allowed text-[#9D96AF]"
                    }
                    flex items-center justify-center font-semibold text-[14px] font-[400] lg:my-10 my-5 px-8 lg:py-[11px] py-[12px] rounded !w-[90%] lg:max-w-[150px] ${isLoading ? "cursor-not-allowed opacity-70" : ""} rounded-full transition duration-300 ease-in-out hover:scale-[1.1]`}
                  disabled={!values.termsAccepted || isLoading}
                  onClick={handlePersonalDetailsSubmit}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Next"
                  )}
                </button>
              </div>
            </div>
          )}
          {currentStep === 3 && (
            <div className="lg:max-w-[628px] w-full lg:p-0 px-6">
              <h2 className="font-plusjakarta block font-semibold text-[32px] leading-[40px] tracking-normal lg:text-center text-[#33125F] lg:my-5 my-2">
                Financial details
              </h2>
              <p className="text-[#423E51] mb-10 font-plusjakarta font-normal text-[16px] leading-[24px] tracking-normal lg:text-center text-left">
                We ask for this to comply with financial regulations and to
                provide you with the best customer experience.
              </p>
              <div className="w-[100%] flex flex-col space-y-6">
                <div className="w-full">
                  <SimpleSelect
                    label="Employment status"
                    placeholder="Select your employment status"
                    options={[
                      "Employed Full-Time",
                      "Employed Part-Time",
                      "Self Employed",
                      "Student",
                      "Retired",
                      "Unemployed",
                      "Other",
                    ]}
                    icon={
                      <img
                        src={angle_down}
                        className="w-[16px] h-[16px]"
                        alt="dropdown"
                      />
                    }
                    name="employmentStatus"
                    value={values.employmentStatus}
                    onChange={handleChange}
                    state={errors?.employmentStatus ? "error" : "default"}
                    helperText={errors?.employmentStatus || ""}
                  />
                </div>
                <div className="w-full">
                  <SimpleSelect
                    label="Primary source of funds"
                    placeholder="Select your primary source"
                    options={[
                      "Salary",
                      "Business Income",
                      "Investment",
                      "Inheritance",
                      "Pension",
                      "Other",
                    ]}
                    icon={
                      <img
                        src={angle_down}
                        className="w-[16px] h-[16px]"
                        alt="dropdown"
                      />
                    }
                    name="primarySourceOfFunds"
                    value={values.primarySourceOfFunds}
                    onChange={handleChange}
                    state={errors?.primarySourceOfFunds ? "error" : "default"}
                    helperText={errors?.primarySourceOfFunds || ""}
                  />
                </div>
                <div className="w-[100%] flex gap-3 lg:flex-row flex-col mb-5 mt-5">
                  <div className="lg:w-[50%] w-full">
                    <SimpleSelect
                      label="Currency"
                      placeholder="Select your currency"
                      options={[
                        "ZAR - South African Rand",
                        "USD - United States Dollar",
                        "EUR - Euro",
                        "JPY - Japanese Yen",
                        "GBP - British Pound Sterling",
                        "AUD - Australian Dollar",
                        "CAD - Canadian Dollar",
                        "CHF - Swiss Franc",
                        "CNY - Chinese Yuan",
                        "HKD - Hong Kong Dollar",
                        "NZD - New Zealand Dollar",
                      ]}
                      icon={
                        <img
                          src={angle_down}
                          className="w-[16px] h-[16px]"
                          alt="dropdown"
                        />
                      }
                      name="currency"
                      value={values.currency}
                      onChange={handleChange}
                      state={errors?.currency ? "error" : "default"}
                      helperText={errors?.currency || ""}
                    />
                  </div>
                  <div className="lg:w-[50%] w-full">
                    <SimpleSelect
                      label="Monthly salary range"
                      placeholder="Select your salary range"
                      options={[
                        "Less than 10 000",
                        "10 000 - 15 000",
                        "15 000 - 25 000",
                        "25 000 - 55 000",
                        "55 000 - 100 000",
                        "100 000 +",
                      ]}
                      icon={
                        <img
                          src={angle_down}
                          className="w-[16px] h-[16px]"
                          alt="dropdown"
                        />
                      }
                      name="monthlySalaryRange"
                      value={values.monthlySalaryRange}
                      onChange={handleChange}
                      state={errors?.monthlySalaryRange ? "error" : "default"}
                      helperText={errors?.monthlySalaryRange || ""}
                    />
                  </div>
                </div>
                <div className="w-[100%] gap-5 flex justify-center items-center">
                  <button
                    className={`bg-transparent border border-[#EBE9EF] text-[#6D6584] flex items-center justify-center font-semibold text-[14px] font-[400] lg:my-10 my-5 px-8 lg:py-[11px] py-[12px] rounded !w-[90%] lg:max-w-[150px] rounded-full lg:block hidden transition duration-300 ease-in-out hover:scale-[1.1] hover:border-[#57249C] hover:text-[#57249C]`}
                    onClick={backToPreviousStep}
                  >
                    Back
                  </button>
                  <button
                    className={`
                      ${
                        !isLoading
                          ? "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white"
                          : "bg-[#F2F1F4] cursor-not-allowed text-[#9D96AF]"
                      }
                      flex items-center justify-center font-semibold text-[14px] font-[400] lg:mt-10 mt-[20%] lg:mb-10 mb-5 px-8 lg:py-[11px] py-[12px] rounded !w-[90%] lg:max-w-[150px] ${isLoading ? "cursor-not-allowed opacity-70" : ""} rounded-full transition duration-300 ease-in-out hover:scale-[1.1] `}
                    disabled={isLoading}
                    onClick={handleFinancialDetailsSubmit}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      "Next"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {currentStep === 4 && (
            <div className="lg:max-w-[356px] flex flex-col w-full lg:p-0 px-6">
              <div className="w-[100%] lg:mt-[100px] mt-[10px] flex flex-col justify-center items-center space-y-6 lg:order-1 order-3">
                <img src={veriff} alt="Success" className="w-[146px]" />
                <img src={camera} alt="Camera" className="w-[194px]" />
              </div>
              <p className="font-plusjakarta font-semibold text-[32px] leading-[40px] tracking-normal lg:text-center text-left text-[#33125F] lg:mt-7 mt-3 lg:mb-7 mb-3 lg:order-2 order-1">
                {veriffStatus === "approved"
                  ? "Verification Complete!"
                  : "Are you ready for your close-up?"}
              </p>
              <p className="font-plusjakarta font-normal text-[16px] leading-[24px] tracking-normal lg:text-center text-left mb-7 text-[#423E51] lg:order-3 order-2">
                {veriffStatus === "approved" ? (
                  "Your identity has been successfully verified. You can now proceed to use all features."
                ) : (
                  <>
                    Using <span className="font-semibold">Veriff</span>, we're
                    going to verify your identity using a selfie and a photo of
                    your ID.
                  </>
                )}
              </p>

              {canStartVerification() && (
                <div className="w-[100%] gap-4 flex flex-col justify-center items-center lg:mt-0 lg:mt-10 mt-[20%] lg:mb-10 mb-5 lg:order-4 order-4">
                  <button
                    onClick={initiateVeriff}
                    disabled={isLoading}
                    className={`bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white flex items-center justify-center font-semibold text-[14px] font-[400] px-8 lg:py-[11px] py-[12px] rounded w-[308px] lg:w-[358px] rounded-full transition duration-300 ease-in-out hover:scale-[1.1]  ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isLoading ? "Loading..." : getVerificationButtonText()}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-auto  border border-[#EBE9EF] flex justify-center items-center left-0 w-full lg:px-[23px] px-[27px] lg:pt-[23px] pt-[15px] lg:pb-[23px] pb-[35px]">
          <p className="font-plusjakarta font-normal text-[16px] leading-[24px] tracking-normal text-[#423E51]">
            Already have an account?{" "}
            <p
              onClick={handleLogout}
              className="text-[#33125F] font-semibold ml-2 inline cursor-pointer"
            >
              Sign in
            </p>
          </p>
        </div>
      </div>
      {showStatusPopup && (
        <div className="flex justify-center min-h-[100vh] bg-black/20 items-center fixed top-0 w-full z-[100]">
          <div className="lg:w-[500px] w-[95%] min-h-[300px] px-6 lg:px-8 bg-white flex flex-col items-center justify-center gap-6 rounded-2xl shadow-xl py-8">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center ${veriffStatus === "approved" ? "bg-green-100" : veriffStatus === "declined" ? "bg-red-100" : "bg-blue-100"}`}
            >
              {veriffStatus === "approved" ? (
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              ) : veriffStatus === "declined" ? (
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              ) : (
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              )}
            </div>

            <p className="text-[#33125F] font-semibold text-[24px] text-center">
              {veriffStatus === "approved"
                ? "Verification Approved!"
                : veriffStatus === "declined"
                  ? "Verification Declined"
                  : "Verification in Progress"}
            </p>

            <div className="max-w-[400px] px-4">
              <p className="text-[#423E51] text-[16px] text-center leading-relaxed">
                {statusPopupMessage}
              </p>
            </div>

            <div className="flex gap-4 mt-4">
              <button
                className="bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
                onClick={async () => {
                  if (veriffStatus === "approved") {
                    await setAllSet(true);
                  }
                  if (veriffStatus === "declined") {
                    await setShowStatusPopup(false);
                    await setLoader(true);
                    await initiateVeriff();
                    await checkVeriffStatus(true);
                    return;
                  }
                  await setShowStatusPopup(false);
                  await checkVeriffStatus(true);
                }}
              >
                {veriffStatus === "approved"
                  ? "Continue"
                  : veriffStatus === "declined"
                    ? "Resubmit Verification"
                    : "Sync Veriff"}
              </button>
            </div>
          </div>
        </div>
      )}

      {allSet && <AllSet />}
      {(activeCountryModal || notify) && (
        <div className="flex justify-center min-h-[100vh] bg-black/20 items-center fixed top-0 w-full z-[100]">
          <div className="lg:w-[735px] w-[95%] lg:h-[635px] h-[70vh] px-6 lg:px-0 bg-white flex flex-col items-center justify-center gap-4 rounded-2xl shadow-xl">
            <img src={sync} className="w-[194px]" alt="sync" />
            {activeCountryModal && (
              <>
                <p className="text-[#33125F] font-semibold text-[24px] text-center">
                  We're not in your country just yet.
                </p>
                <div className="max-w-[556px] px-4">
                  <p className="text-[#423E51] text-[14px] text-center">
                    We're working hard to bring 80eight to more countries around
                    the world.
                    <br />
                    Leave your details and we'll let you know the moment we
                    launch in your region.
                    <br />
                  </p>
                  <p className="text-[#423E51] text-[14px] text-center mt-4">
                    You'll be first in line when we go live.
                  </p>
                </div>
                <button
                  className={`bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] mt-2 text-white flex items-center justify-center font-semibold text-[14px] font-[400] px-8 lg:py-[11px] py-[12px] rounded rounded-full transition duration-300 ease-in-out hover:scale-[1.1] `}
                  onClick={() => {
                    setActiveCountryModal(false);
                    setNotify(true);
                    handleFinancialDetailsSubmit();
                  }}
                >
                  Notify me
                </button>
              </>
            )}
            {notify && (
              <>
                <p className="text-[#33125F] font-semibold text-[24px] text-center">
                  You're on the list!
                </p>
                <div className="max-w-[556px] px-4">
                  <p className="text-[#423E51] text-[14px] text-center">
                    Thanks for signing up. We're building something incredible
                    and can't wait to welcome you soon.
                  </p>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                  }}
                  className={`bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] mt-2 text-white flex items-center justify-center font-semibold text-[14px] font-[400] px-8 lg:py-[11px] py-[12px] rounded rounded-full transition duration-300 ease-in-out hover:scale-[1.1] `}
                >
                  Back to home page
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <Toaster />
      {loader && <SpinningLoader />}
    </>
  );
}
